import { useState } from "react";
import { Flag } from "lucide-react";
import type { Area, Project, Space } from "@desk/contracts";
import { DatePicker } from "@/components/ui/date-picker";
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { deadlineText } from "@/components/task-bits";
import { useHierarchy } from "@/data/hooks";
import { deadlineState } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { useContainerOps } from "./ContainerActions";
import { IconDialog } from "./IconDialog";

type Target = { kind: "area"; area: Area } | { kind: "space"; space: Space } | { kind: "project"; project: Project };

export { NAME_MAX } from "./ContainerActions";

/** Page-header rename for a container (see `EditableTitle`). Resolves false when the save failed. */
export function useRenameContainer(target: Target | null) {
  const { rename } = useContainerOps();
  return (name: string) => (target ? rename(target, name) : Promise.resolve(false));
}

/** The header icon's picker for an area or space: returns the click handler and the dialog to render. */
export function useHeaderIcon(target: { kind: "area"; area: Area } | { kind: "space"; space: Space } | null) {
  const [open, setOpen] = useState(false);
  return {
    onIconClick: () => setOpen(true),
    dialog: open && target ? <IconDialog target={target} open onOpenChange={setOpen} /> : null,
  };
}

const chip =
  "inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-[12.5px] text-subtle outline-none transition-colors hover:bg-hover hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40 data-[state=open]:bg-hover [&_svg]:size-3.5";

/** Planned ↔ Active, shown in the project header. Completed projects reopen from the "…" menu instead. */
export function ProjectStateChip({ project }: { project: Project }) {
  const { api, run } = useContainerOps();
  if (project.state === "completed") return <span className="px-2 text-[12.5px] text-subtle">Completed</span>;
  const set = (state: "planned" | "active") =>
    state !== project.state &&
    void run("change the project state", () => api.updateProject(project.id, { state }), undefined, undefined, {
      apply: [{ kind: "project", id: project.id, patch: { state } }],
    });
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button type="button" className={chip} aria-label={`State: ${project.state === "planned" ? "Planned" : "Active"}`}>
          {project.state === "planned" ? "Planned" : "Active"}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        <DropdownMenuRadioGroup value={project.state} onValueChange={(v) => set(v as "planned" | "active")}>
          <DropdownMenuRadioItem value="planned">Planned</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="active">Active</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The project's deadline in the header, colored like task deadlines; a flag button sets one. */
export function ProjectDeadlineChip({ project }: { project: Project }) {
  const { api, run } = useContainerOps();
  const h = useHierarchy();
  const dl = project.deadline;
  const state = dl ? deadlineState(dl, h.today) : null;
  const picker = (trigger: React.ReactElement) => (
    <DatePicker
      value={dl}
      today={h.today}
      clearLabel="No deadline"
      align="end"
      onChange={(d) =>
        d !== dl &&
        void run("set the deadline", () => api.updateProject(project.id, { deadline: d }), undefined, undefined, {
          apply: [{ kind: "project", id: project.id, patch: { deadline: d } }],
        })
      }
    >
      {trigger}
    </DatePicker>
  );
  if (!dl) {
    return picker(
      <button type="button" className={cn(chip, "px-1.5")} aria-label="Set deadline" title="Set deadline">
        <Flag />
      </button>,
    );
  }
  return picker(
    <button
      type="button"
      className={cn(chip, state === "overdue" && "font-medium text-overdue hover:text-overdue", state === "today" && "font-medium text-due hover:text-due")}
      aria-label={`Deadline: ${deadlineText(dl, h.today)}`}
    >
      <Flag />
      {deadlineText(dl, h.today)}
    </button>,
  );
}
